// docs/112 — item upgrades in words and looks. An item row's upgrades (`item_upgrade.applied`, ids in
// the order they were laid on, a repeat being a rank) become ranks ("Emberkiss II"), lines of what
// each adds at its rank (numbers × rank, powers scaled as fk_grammar `Power::scaled`, the ability at
// its rank), the `+N` its name wears, and its look: the glow of its strongest enchantment and the
// badge of its strongest improvement. Pure; the rules (what fits, what it costs) are the wasm's
// `upgrade_options`. Covered by upgrades.test.ts.
import { upgradeSpec, upgradeSpecs, type CatalogueItem, type ItemStats, type PowerSpec, type UpgradeKind, type UpgradeSpec } from './catalogue';
import { abilityWords, powerLine, statLines, type AbilityWords } from './describe';

/** The workshops (docs/112 UP6) in `fk_grammar::UpgradeShop` order, as the client names them. */
export const SHOPS = ['enchanter', 'hedgewitch', 'runecarver', 'stargazer', 'weaponsmith', 'armourer', 'bowyer', 'leatherworker', 'smithy', 'temple', 'dyer'] as const;
export type ShopKey = (typeof SHOPS)[number];
export const SHOP_TITLES: Record<ShopKey, string> = {
  enchanter: "Enchanter's Tower", hedgewitch: "Hedge-Witch's Cottage", runecarver: "Runecarver's Hall", stargazer: "Stargazer's Observatory",
  weaponsmith: "Bladewright's Forge", armourer: "Armourer's Yard", bowyer: 'Bowyer & Fletcher', leatherworker: 'Tanner & Tailor',
  smithy: 'The smithy', temple: 'The temple', dyer: "Dyer's Yard",
};
/** What each workshop's keeper does, in a line (the counter's head). */
export const SHOP_TRADES: Record<ShopKey, string> = {
  enchanter: 'Fire, frost and lightning laid on steel; spellcraft for staves, wands and rings.',
  hedgewitch: 'Blood, venom and root: hexes that bite and charms that mend.',
  runecarver: 'Dwarven runes cut deep: wards, resists, thorns and standing fast.',
  stargazer: 'Moon and star: sight in the dark, luck, lore and a first blow that lands true.',
  weaponsmith: 'Melee weapons honed, re-forged, balanced and serrated.',
  armourer: 'Heavy armour, shields and helms reinforced, riveted, padded and spiked.',
  bowyer: 'Bows and crossbows re-strung, fletched, sighted and barbed.',
  leatherworker: 'Cloth and leather oiled, lined, hobnailed and re-stitched.',
  smithy: 'The everyday work any village smith can do.',
  temple: 'Blessings laid on arms and armour.',
  dyer: 'Clothes dyed in any of twelve colours, or washed back to their own. Metal, stone, bone and the like keep theirs.',
};
export const shopKey = (u8: number): ShopKey => SHOPS[u8] ?? 'smithy';
export const shopKind = (k: ShopKey): UpgradeKind => (['weaponsmith', 'armourer', 'bowyer', 'leatherworker', 'smithy'].includes(k) ? 'improve' : 'enchant');

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
export const roman = (n: number): string => ROMAN[n] ?? String(n);

/** One upgrade on a piece, at its rank. */
export interface UpgradeRank { up: UpgradeSpec; rank: number }

/** `[3, 5, 3]` → Emberkiss II, Honed I (first-applied order; unknown ids dropped): fk_grammar `upgrade_ranks`. */
export function ranksOf(applied: readonly number[] | undefined): UpgradeRank[] {
  const out: UpgradeRank[] = [];
  for (const id of applied ?? []) {
    const up = upgradeSpec(id);
    if (!up) continue;
    const r = out.find((x) => x.up.id === id);
    if (r) r.rank = Math.min(up.max_rank, r.rank + 1);
    else out.push({ up, rank: 1 });
  }
  return out;
}

/** "Emberkiss II" (a rank-1 upgrade with one rank only is just its name). */
export const rankName = (r: UpgradeRank): string => (r.up.max_rank === 1 ? r.up.name : `${r.up.name} ${roman(r.rank)}`);

/** An upgraded piece's name: "Emberwake Longsword +3" (the count of upgrades laid on it). */
export function upgradedName(name: string, applied: readonly number[] | undefined): string {
  const n = applied?.length ?? 0;
  return n ? `${name} +${n}` : name;
}

/** A power `n` times over: fk_grammar `Power::scaled` (tools/items/upgrades.mjs `scalePower`). */
export function scalePower(p: PowerSpec, n: number): PowerSpec {
  const [k] = Object.keys(p);
  const v = p[k!] as Record<string, number> | number;
  if (n <= 1 || ['immune', 'trait', 'keen_search', 'carried'].includes(k!)) return p;
  if (typeof v === 'number') return { [k!]: ['purse', 'lore', 'haggle'].includes(k!) ? Math.min(100, v * n) : v * n };
  switch (k) {
    case 'on_hit': case 'opening': case 'aura': return { [k]: { ...v, magnitude: (v['magnitude'] ?? 0) * n } };
    case 'bane': return { [k]: { ...v, dmg: (v['dmg'] ?? 0) * n } };
    case 'resist': return { [k]: { ...v, amount: (v['amount'] ?? 0) * n } };
    case 'reaper': return { [k]: { heal: (v['heal'] ?? 0) * n, resource: (v['resource'] ?? 0) * n } };
    default: return p;
  }
}

/** A rank's numbers: the per-rank stats × rank. */
export function rankStats(up: UpgradeSpec, rank: number): ItemStats {
  const out: ItemStats = {};
  for (const [k, v] of Object.entries(up.per_rank.stats ?? {})) out[k as keyof ItemStats] = (v ?? 0) * rank;
  return out;
}

/** What an upgrade gives at a rank, in words: its numbers, its powers, and its ability. */
export interface UpgradeWords { name: string; kind: UpgradeKind; rank: number; lines: string[]; ability?: AbilityWords }
export function describeUpgrade(up: UpgradeSpec, rank: number): UpgradeWords {
  const r = Math.max(1, Math.min(up.max_rank, rank));
  const lines = [...statLines(rankStats(up, r)), ...(up.per_rank.powers ?? []).map((p) => powerLine(scalePower(p, r)))];
  const a = up.active?.[Math.min(up.active.length, r) - 1];
  return { name: up.max_rank === 1 ? up.name : `${up.name} ${roman(r)}`, kind: up.kind, rank: r, lines, ...(a ? { ability: abilityWords(a) } : {}) };
}

/** Every upgrade on a piece, in words (for its card). */
export const upgradeWords = (applied: readonly number[] | undefined): UpgradeWords[] => ranksOf(applied).map((r) => describeUpgrade(r.up, r.rank));

/** What the next rank adds beyond this one: "+1 power" for a stat upgrade going from I to II. */
export function nextRankLines(up: UpgradeSpec, rank: number): string[] {
  return describeUpgrade(up, rank).lines;
}

/** docs/112 UP9: how an upgraded piece looks — its strongest enchantment's glow and particles, its
 *  strongest improvement's badge. Strongest = rarity × rank, the first applied on a tie. */
export interface UpgradeLook { glow?: string; particles?: string; badge?: string; enchanted: boolean; improved: boolean; count: number }
export function lookOf(applied: readonly number[] | undefined): UpgradeLook {
  const rs = ranksOf(applied);
  const best = (kind: UpgradeKind) => rs.filter((r) => r.up.kind === kind).reduce<UpgradeRank | undefined>((b, r) => (!b || r.up.rarity * r.rank > b.up.rarity * b.rank ? r : b), undefined);
  const e = best('enchant'), i = best('improve');
  return {
    ...(e?.up.look.glow ? { glow: e.up.look.glow } : {}), ...(e?.up.look.particles ? { particles: e.up.look.particles } : {}),
    ...(i ? { badge: i.up.look.badge ?? 'anvil' } : {}),
    enchanted: !!e, improved: !!i, count: applied?.length ?? 0,
  };
}

/** Every upgrade a workshop of this trade can sell (the upgrade book). */
export const soldAt = (shop: ShopKey): UpgradeSpec[] => upgradeSpecs().filter((u) => u.shops.includes(shop));
/** The enchantments a wonder may give (docs/112 UP8). */
export const giftable = (): UpgradeSpec[] => upgradeSpecs().filter((u) => u.gift);

/** "Fits swords, axes and daggers" from `fits`. */
const FIT_WORDS: Record<string, string> = {
  worn: 'anything worn', weapon: 'weapons', melee: 'melee weapons', blade: 'blades (swords, axes, daggers)', ranged: 'bows and crossbows',
  caster: 'staves, wands and foci', jewel: 'rings, charms and amulets', body: 'armour and clothing', heavy: 'heavy armour, shields and helms',
  light: 'light armour and clothing', 'armour:cloth': 'cloth armour', 'armour:leather': 'leather armour', 'armour:chain': 'chain armour',
  'armour:plate': 'plate armour', 'helmet:cap': 'caps and hoods', 'helmet:kettle': 'kettle helms', 'helmet:great': 'great helms',
  armour: 'body armour', helmet: 'helms', boots: 'boots', gloves: 'gloves', belt: 'belts', cape: 'capes', legs: 'legwear', neck: 'amulets',
  ring: 'rings', charm: 'charms', focus: 'foci', wand: 'wands', staff: 'staves', sword: 'swords', axe: 'axes', mace: 'maces', dagger: 'daggers',
  spear: 'spears', bow: 'bows', crossbow: 'crossbows', shield: 'shields',
};
export function fitsLine(up: UpgradeSpec): string {
  const w = up.fits.map((f) => FIT_WORDS[f] ?? f);
  return `Fits ${w.length > 1 ? `${w.slice(0, -1).join(', ')} and ${w[w.length - 1]}` : w[0]}`;
}

/** A plain-words list of everything an item's upgrades add, for one-line tooltips. */
export function upgradeTooltipLines(applied: readonly number[] | undefined): string[] {
  return upgradeWords(applied).map((w) => `✦ ${w.name}: ${[...w.lines, ...(w.ability ? [`${w.ability.name} (${w.ability.effects.join('; ')})`] : [])].join('; ')}`);
}

/** The family keys a catalogue piece answers to: its family, and `family:weight` for armour and helmets. */
const keysOf = (it: CatalogueItem): string[] => [it.family, ...(it.weight ? [`${it.family}:${it.weight}`] : [])];
const GROUPS: Record<string, readonly string[]> = {
  worn: ['sword', 'axe', 'mace', 'dagger', 'spear', 'bow', 'crossbow', 'staff', 'wand', 'shield', 'focus', 'armour', 'helmet', 'cape', 'gloves', 'boots', 'legs', 'belt', 'neck', 'ring', 'charm'],
  weapon: ['sword', 'axe', 'mace', 'dagger', 'spear', 'bow', 'crossbow', 'staff', 'wand'],
  melee: ['sword', 'axe', 'mace', 'dagger', 'spear', 'staff'], blade: ['sword', 'axe', 'dagger'], ranged: ['bow', 'crossbow'],
  caster: ['staff', 'wand', 'focus'], jewel: ['ring', 'charm', 'neck'], body: ['armour', 'helmet', 'cape', 'gloves', 'boots', 'legs', 'belt'],
  heavy: ['armour:chain', 'armour:plate', 'helmet:kettle', 'helmet:great', 'shield'],
  light: ['armour:cloth', 'armour:leather', 'helmet:cap', 'cape', 'gloves', 'boots', 'legs', 'belt'],
};
/** Whether an upgrade fits a catalogue piece (tools/items/upgrades.mjs `fits`; the rules' own check is
 *  the wasm's `upgrade_options`, which also knows the hand-authored pieces). For the book and tests. */
export function fitsItem(up: UpgradeSpec, it: CatalogueItem): boolean {
  if (!(it.upgrades ?? []).includes(up.kind)) return false;
  const keys = new Set(up.fits.flatMap((f) => GROUPS[f] ?? [f]));
  return keysOf(it).some((k) => keys.has(k) || keys.has(k.split(':')[0]!));
}
